import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  flexRender,
} from "@tanstack/react-table";
import classNames from "classnames";
import GridLoader from "react-spinners/GridLoader";

interface TableProps {
  isSearchable?: boolean;
  isSortable?: boolean;
  pagination?: boolean;
  isExpandable?: boolean;
  columns: any[];
  data: any[];
  tableClass?: string;
  theadClass?: string;
  isFetching?: boolean;
  pageSize?: number;
}

const Table = ({
  isSearchable = false,
  isSortable = false,
  pagination = false,
  isExpandable = false,
  columns,
  data,
  tableClass,
  theadClass,
  isFetching = false,
  pageSize = 20
}: TableProps) => {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: isSearchable ? getFilteredRowModel() : undefined,
    getSortedRowModel: isSortable ? getSortedRowModel() : undefined,
    getPaginationRowModel: pagination ? getPaginationRowModel() : undefined,
    getExpandedRowModel: isExpandable ? getExpandedRowModel() : undefined,
    initialState: pagination
      ? {
        pagination: {
          pageSize: pageSize,
        },
      }
      : undefined,
  });

  const rows = pagination
    ? table.getPaginationRowModel().rows
    : table.getRowModel().rows;

  const isStickyHeader = theadClass?.includes("sticky-table-header");

  return (
    <table

      className={classNames(
        "table table-centered table-responsive mb-0 border-0",
        isStickyHeader ? "" : "rounded mt-3",
        isFetching ? "" : "border",
        tableClass
      )}
    >
      <thead className={classNames("table-alt-color", isStickyHeader ? "" : "border ", theadClass)}>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                className={classNames({
                  sorting_asc: header.column.getIsSorted() === "asc",
                  sorting_desc: header.column.getIsSorted() === "desc",
                  sortable: isSortable,
                })}
                onClick={header.column.getToggleSortingHandler()}
                style={{ cursor: isSortable ? "pointer" : undefined, width: header.column.columnDef.size ? header.column.columnDef.size : 'auto' }}
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {isFetching ? (
          <tr>
            <td colSpan={columns.length}>
              <div className="flex justify-center items-center h-64">
                <GridLoader color="#1f3260" />
              </div>
            </td>
          </tr>
        ) : (
          <>
            {rows.map((row) => (
              <tr
                key={row.id}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    style={
                      cell.column.columnDef.size
                        ? { width: cell.column.columnDef.size }
                        : undefined
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </>
        )}
      </tbody>
    </table>
  );
};

export default Table;
